import { REMINDERS_PAUSED_REASONS } from '@shared/core/billing'
import classNames from 'classnames'
import { format } from 'date-fns'
import { AlertTriangle, BellOff, CalendarCheck, Check, Pause } from 'lucide-react'
import { FormattedText } from 'src/modules/core/components'
import type { FreePlanStatusCardProps } from './free-plan-status-card.interface'
import classes from './free-plan-status-card.module.scss'

const INCLUDED_FEATURE_IDS = [
  'payment.plans.freePlan.unlimitedBookings',
  'payment.plans.freePlan.unlimitedAppointmentTypes',
  'payment.plans.freePlan.easyRescheduling',
]

export function FreePlanStatusCard({
  sentCount,
  limit,
  isPaused,
  isRunningLow,
  notSentCount,
  reason,
  endedAt,
}: FreePlanStatusCardProps) {
  return (
    <section className={classes.card} data-testid="free-plan-status-card">
      <div className={classes.summary}>
        <div className={classes.stats}>
          <FreePlanTitle isPaused={isPaused} isRunningLow={isRunningLow} />
          <span className={classes.divider} />
          {reason === REMINDERS_PAUSED_REASONS.subscriptionEnded ? (
            <SubscriptionEnded endedAt={endedAt} />
          ) : (
            <FreePlanUsage sentCount={sentCount} limit={limit} isPaused={isPaused} isRunningLow={isRunningLow} />
          )}
          {isPaused && notSentCount !== undefined && (
            <>
              <span className={classes.divider} />
              <NotSentReminders count={notSentCount} />
            </>
          )}
        </div>
        {isPaused && (
          <p className={classes.resume}>
            <FormattedText id="payment.plans.freePlan.resume" />
          </p>
        )}
      </div>
      <FreePlanIncludes />
    </section>
  )
}

function FreePlanTitle({ isPaused, isRunningLow }: Pick<FreePlanStatusCardProps, 'isPaused' | 'isRunningLow'>) {
  return (
    <div className={classes.plan}>
      <div className={classes.titleRow}>
        <span className={classes.title}>
          <FormattedText id="payment.plans.freePlan.title" />
        </span>
        {isPaused && (
          <span className={classes.pausedBadge}>
            <Pause size={10} fill="currentColor" strokeWidth={0} className={classes.pausedIcon} aria-hidden />
            <FormattedText id="payment.plans.freePlan.paused" />
          </span>
        )}
        {!isPaused && isRunningLow && (
          <span className={classes.pausedBadge} data-testid="free-plan-running-low-badge">
            <AlertTriangle size={11} strokeWidth={2.4} className={classes.pausedIcon} aria-hidden />
            <FormattedText id="payment.plans.freePlan.runningLow" />
          </span>
        )}
      </div>
      <span className={classes.caption}>
        <FormattedText id="payment.plans.freePlan.currentPlan" />
      </span>
    </div>
  )
}

function SubscriptionEnded({ endedAt }: Pick<FreePlanStatusCardProps, 'endedAt'>) {
  return (
    <div className={classes.ended}>
      <FormattedText id="payment.plans.freePlan.ended" values={{ date: endedAt && format(endedAt, 'MMMM d, yyyy') }} />
    </div>
  )
}

function FreePlanUsage({ sentCount, limit, isPaused, isRunningLow }: Omit<FreePlanStatusCardProps, 'notSentCount'>) {
  const usedPercent = limit > 0 ? Math.min(100, (sentCount / limit) * 100) : 100

  return (
    <div className={classes.usage}>
      <div className={classes.usageLabel}>
        <FormattedText id="payment.plans.freePlan.used" />
        <strong>
          <FormattedText id="payment.plans.freePlan.usedCount" values={{ sent: sentCount, limit }} />
        </strong>
      </div>
      <div className={classes.usageTrack} data-testid="free-plan-usage-track">
        <div
          className={classNames(classes.usageFill, {
            [classes.usageFillPaused]: isPaused,
            [classes.usageFillRunningLow]: !isPaused && isRunningLow,
          })}
          style={{ width: `${usedPercent}%` }}
        />
      </div>
    </div>
  )
}

function NotSentReminders({ count }: { count: number }) {
  return (
    <div className={classes.notSent}>
      <BellOff size={26} strokeWidth={1.8} className={classes.notSentIcon} aria-hidden />
      <div>
        <div className={classes.notSentCount}>{count}</div>
        <div className={classes.notSentLabel}>
          <FormattedText id="payment.plans.freePlan.notSent" values={{ count }} />
        </div>
      </div>
    </div>
  )
}

function FreePlanIncludes() {
  return (
    <div className={classes.includes}>
      <div className={classes.includesTitle}>
        <CalendarCheck size={16} strokeWidth={1.8} className={classes.tealIcon} aria-hidden />
        <FormattedText id="payment.plans.freePlan.includesBooking" />
      </div>
      <span className={classes.includesDivider} />
      {INCLUDED_FEATURE_IDS.map((id) => (
        <div key={id} className={classes.includesItem}>
          <Check size={12} strokeWidth={2.2} className={classes.tealIcon} aria-hidden />
          <FormattedText id={id} />
        </div>
      ))}
    </div>
  )
}
